import React from 'react';
import { useParams } from 'react-router-dom';

import { EAdSenseProvider } from '@domain/enums/common/EAdSense';
import { ESynchronizationType } from '@domain/enums/hooks/ESynchronization';
import { IParams } from '@domain/interfaces/IParams';
import { IAdSenseAccount } from '@domain/interfaces/dashboard/AdSense/IAdSenseAccount';
import {
  IFacebookAdAccountsProviderProps,
  IFacebookAdAccountsProvider,
} from '@domain/interfaces/integrations/marketing/facebook/IFacebookAdAccounts';

import { useToast } from '@helpers/hooks/useToast';
import { useSynchronization } from '@helpers/hooks/common/useSynchronization';
import { useFacebookCredentials } from '@helpers/hooks/common/integrations/marketing/facebook/useFacebookCredentials';

import { ADS_PROVIDERS } from '@constants/common/integrations';

import facebookAccountService from '@services/pages/dashboard/adSense/facebook/facebookAccount';
import adSenseAccountService from '@services/pages/dashboard/adSense/adSenseAccount';

const FacebookAdAccountsContext = React.createContext<IFacebookAdAccountsProvider | null>(null);

export const FacebookAdAccountsProvider: React.FC<IFacebookAdAccountsProviderProps> = ({
  adSenseCredentialAliasId,
  children,
}) => {
  const { storeAliasId } = useParams<IParams>();
  const { toast } = useToast();
  const { addSynchronization } = useSynchronization();

  const { adAccountSearchName } = useFacebookCredentials();

  const [adSenseAccountsPage, setAdSenseAccountsPage] = React.useState<number>(0);
  const [adSenseAccountPageCount, setAdSenseAccountsPageCount] = React.useState<number>(1);
  const [adSenseAccounts, setAdSenseAccounts] = React.useState<Array<IAdSenseAccount>>([]);
  const [isLoadingAdSenseAccounts, setIsLoadingAdSenseAccounts] = React.useState<boolean>(false);
  const [isAdSenseAccountsError, setIsAdSenseAccountsError] = React.useState<boolean>(false);

  const loadAdSenseAccounts = React.useCallback(async () => {
    setIsLoadingAdSenseAccounts(true);
    setIsAdSenseAccountsError(false);

    try {
      const { data: adSenseAccountsData } = await adSenseAccountService.listAdSenseAccountsPromise({
        storeAliasId,
        adSenseCredentialAliasId,
        page: adSenseAccountsPage + 1,
        filter: {
          provider: EAdSenseProvider.FACEBOOK,
          name: adAccountSearchName,
        },
      });

      if (adSenseAccountsData) {
        setAdSenseAccounts(currentAdSenseAccounts => [
          ...currentAdSenseAccounts,
          ...adSenseAccountsData.ad_sense_accounts,
        ]);
        setAdSenseAccountsPageCount(adSenseAccountsData.total_pages);
      }

      setIsLoadingAdSenseAccounts(false);
    } catch (error: any) {
      setIsLoadingAdSenseAccounts(false);
      setIsAdSenseAccountsError(true);
      toast.error(error?.response?.data?.message);
    }
  }, [adAccountSearchName, adSenseAccountsPage, storeAliasId, adSenseCredentialAliasId, toast]);

  const activateAdSenseAccount = React.useCallback(
    (adSenseAccountAliasId, data) => {
      const foundFacebookIntegration = ADS_PROVIDERS.find(
        integration => integration.identifier === EAdSenseProvider.FACEBOOK,
      ) as typeof ADS_PROVIDERS[number];

      const { generateSyncRequest } = foundFacebookIntegration;

      const aliases = {
        adSenseAccountAliasId,
        adSenseCredentialAliasId,
      };

      addSynchronization({
        name: 'Facebook',
        type: ESynchronizationType.UPDATE_AD_SENSE_ACCOUNTS,
        label: 'Perfil',
        dependencies: [],
        storeAliasId,
        externalId: adSenseAccountAliasId,
        config: {
          isOnboardingSynchronization: false,
          showNotification: true,
        },
        request: generateSyncRequest(
          ESynchronizationType.UPDATE_AD_SENSE_ACCOUNTS,
          storeAliasId,
          aliases,
          data,
        ),
      });
    },
    [adSenseCredentialAliasId, addSynchronization, storeAliasId],
  );

  const deactiveAdSenseAccount = React.useCallback(
    async (adSenseAccountAliasId, data) => {
      try {
        await facebookAccountService.updateFacebookAccount({
          storeAliasId,
          adSenseAccountAliasId,
          adSenseCredentialAliasId: adSenseCredentialAliasId || '',
          data,
        });
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      }
    },
    [adSenseCredentialAliasId, storeAliasId, toast],
  );

  const resetInfiniteScroll = React.useCallback(() => {
    setAdSenseAccounts([]);
    setAdSenseAccountsPage(0);
  }, []);

  const updateAdSenseAccount = React.useCallback(
    async (adSenseAccountAliasId, data) => {
      if (data?.is_active) {
        activateAdSenseAccount(adSenseAccountAliasId, data);
      } else {
        await deactiveAdSenseAccount(adSenseAccountAliasId, data);
      }
    },
    [activateAdSenseAccount, deactiveAdSenseAccount],
  );

  const handleLoadMore = React.useCallback(() => {
    setAdSenseAccountsPage(oldPage => oldPage + 1);
  }, []);

  React.useEffect(() => {
    resetInfiniteScroll();
  }, [resetInfiniteScroll, adAccountSearchName]);

  const hasMore = adSenseAccountsPage + 1 < adSenseAccountPageCount;

  return (
    <FacebookAdAccountsContext.Provider
      value={{
        adSenseAccounts,
        handleLoadMore,
        hasMore,
        isAdSenseAccountsError,
        isLoadingAdSenseAccounts,
        loadAdSenseAccounts,
        updateAdSenseAccount,
        resetInfiniteScroll,
      }}
    >
      {children}
    </FacebookAdAccountsContext.Provider>
  );
};

export const useFacebookAdAccounts = (): IFacebookAdAccountsProvider => {
  const context = React.useContext(FacebookAdAccountsContext);

  if (!context) {
    throw new Error('useFacebookAdAccounts must be used within FacebookAdAccountsProvider');
  }

  return context;
};
